import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Mail, Calendar, BookOpen, Archive } from "lucide-react";
import { SEO } from "@/components/SEO";
import api from "@/lib/api";

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function getNumber(title) {
  const m = title.match(/#(\d+)/);
  return m ? parseInt(m[1]) : 0;
}

function fixTitle(title) {
  return title
    .replace("OFALycee", "OFALycée")
    .replace("OFALYCEE", "OFALycée")
    .replace("OFALYCÉE", "OFALycée");
}

function NewsletterCard({ n }) {
  const hasFullContent = n.has_full_content;

  const inner = (
    <div className="group border rounded-xl p-5 hover:border-[#14274B]/40 hover:shadow-md transition-all h-full flex flex-col">
      {n.featured_image && (
        <img src={n.featured_image} alt={n.title} className="w-full h-36 object-cover rounded-lg mb-4" />
      )}
      <h3 className="font-heading text-base mb-2 group-hover:text-[#14274B] transition-colors">
        {fixTitle(n.title)}
      </h3>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
        <Calendar className="h-3.5 w-3.5" />
        <span>{formatDate(n.publication_date)}</span>
      </div>
      <div className="mt-auto">
        {hasFullContent ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ofa-blue group-hover:underline">
            <BookOpen className="h-3 w-3" /> Lire la newsletter
          </span>
        ) : n.newsletter_url ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ofa-blue group-hover:underline">
            <ExternalLink className="h-3 w-3" /> Lire sur Mailchimp
          </span>
        ) : null}
      </div>
    </div>
  );

  if (hasFullContent) {
    return <Link to={`/article/${n.slug}`} data-testid={`newsletter-${n.slug}`}>{inner}</Link>;
  }
  if (n.newsletter_url) {
    return <a href={n.newsletter_url} target="_blank" rel="noopener noreferrer" data-testid={`newsletter-${n.slug}`}>{inner}</a>;
  }
  return <div data-testid={`newsletter-${n.slug}`}>{inner}</div>;
}

export default function NewsletterArchivePage() {
  const [quinzaineItems, setQuinzaineItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/newsletters").then((r) => {
      const items = (r.data || [])
        .filter((n) => n.newsletter_type === "quinzaine")
        .sort((a, b) => getNumber(b.title) - getNumber(a.title));
      setQuinzaineItems(items);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Archives — La Quinzaine d'OFALycée"
        description="Retrouvez toutes les éditions de La Quinzaine d'OFALycée : articles, quiz, actualités et ressources sur l'éducation bilingue."
        url="/newsletter"
      />
      <div data-testid="newsletter-archive-page">

        {/* HERO */}
        <section className="relative overflow-hidden bg-[#14274B] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(45,120,246,0.15),_transparent_60%)]" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-ofa-blue/20 flex items-center justify-center">
                <Archive className="h-7 w-7 text-ofa-blue" />
              </div>
              <div>
                <span className="text-sm font-mono tracking-widest uppercase text-ofa-blue block" data-testid="newsletter-label">Archives</span>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl" data-testid="newsletter-title">La Quinzaine d'OFALycée</h1>
              </div>
            </div>
            <p className="text-lg text-white/60 max-w-2xl leading-relaxed">
              Les premières éditions de notre newsletter bimensuelle. Retrouvez les articles, quiz et ressources
              qui ont marqué les débuts d'OFALycée.
            </p>
            <div className="mt-6">
              <Link to="/rubrique/newsletter-archive" className="text-sm text-ofa-blue hover:underline">
                Voir les éditions actuelles — La Toile d'OFALycée →
              </Link>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Chargement...</div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 bg-[#14274B] rounded-full" />
              <h2 className="font-heading text-2xl md:text-3xl">La Quinzaine d'OFALycée</h2>
              <span className="text-sm text-muted-foreground ml-2">({quinzaineItems.length} éditions)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {quinzaineItems.map((n) => (
                <NewsletterCard key={n.slug} n={n} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
